/**
 * Phase 6c long-tail PR 10 — `<SeasonRail />` (canonical, shared).
 *
 * Hoist of two prior modules into one shared component:
 *
 *   src/components/season/TodaySeasonRail.tsx (Phase 6c PR 5)
 *     used by /v2/today
 *
 *   src/components/plan/SeasonRail.tsx (Phase 6d PR 10)
 *     used by /v2/plan/week/:n
 *
 * The two had the same anatomy — header (kicker + season name +
 * summary) → 5 phase groups → per-week rows → foot links — but
 * differed in:
 *
 *   1. Foot link configuration (Today: 1 link · Plan: 2 links)
 *   2. Current-week row decoration (Today: " — now" suffix · Plan: none)
 *
 * Both diffs are now props. Both consumers import this canonical
 * component and pass route-specific config.
 *
 * Anatomy:
 *
 *   ┌────────────────────────────────┐
 *   │ SEASON      ✓ Approved         │
 *   │ Spring 2026 XC                 │
 *   │ 7 weeks · Apr 6 to May 24 · 2 m│
 *   ├────────────────────────────────┤
 *   │ ▍ BASE              2 wks      │
 *   │   W1   Apr 6                   │
 *   │   W2   Apr 13                  │
 *   │ ▍ BUILD             2 wks      │
 *   │   W3   Apr 20    🏁            │
 *   │   W4   Apr 27                  │
 *   │ ▍ COMPETITION       1 wk       │
 *   │   W5   May 4 — now             │
 *   │ ...                            │
 *   ├────────────────────────────────┤
 *   │ Open season plan ›             │
 *   └────────────────────────────────┘
 *
 * The outer aside carries `data-seasonrail="true"` as a stable hook
 * for `:has()` selectors in route-level CSS. CSS Modules class hashes
 * vary across files, so a data attribute is the portable cross-module
 * hook.
 *
 * Visibility (R-Train-2 / R-Ultra-4, 2026-09-09): the component gates
 * itself in CSS at 1024 and nowhere else. /v2/plan/week/:n mounts it in
 * the desktop shell's RIGHT rail at every desktop width; /v2/today mounts
 * it in its own right rail, which that route's CSS still opens at Ultra.
 * No prop gating at the component.
 */

import { Link } from 'react-router-dom';
import type { MacroPlanWeek } from '../../hooks/useMacroPlan';
import { formatWeekRange } from '../../lib/formatWeekRange';
import { formatWeekDate, groupPhases } from '../../lib/macroPlanPhases';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import { useSeasonPath } from '../../lib/train/routes';
import styles from './SeasonRail.module.css';

export interface SeasonRailFootLink {
  /** React Router `to` (relative or absolute path). */
  to: string;
  /** Display text — caller adds the chevron / arrow if desired. */
  label: string;
}

export interface SeasonRailProps {
  /** Display name shown in the head ("Spring 2026 XC"). Falls back to
   *  "Season" when null. Decoupled from `ActiveSeason` so callers can
   *  pass a string directly. */
  seasonName: string | null;
  /** Macro-plan weeks. Returns null when empty. */
  weeks: MacroPlanWeek[];
  /** Current week (1-based) — outlined. Null hides the highlight. */
  currentWeek: number | null;
  /** Optional approval status — flips the "✓ Approved" badge. */
  approved?: boolean;
  /** Foot links — caller passes route-specific configuration.
   *  Defaults to a single "Open season plan ›" link to /plan when
   *  omitted (matches /v2/today's expected behavior). */
  footLinks?: SeasonRailFootLink[];
  /** Optional decoration appended to the current-week date label
   *  (e.g., " — now"). When omitted, no decoration. /v2/today uses
   *  " — now" because its currentWeek = the actual now-week; /v2/plan/
   *  week/:n omits because currentWeek = the URL's :n (might not be
   *  the actual now-week). */
  currentWeekSuffix?: string;
}

const PHASE_GROUP_CLASS: Record<string, string> = {
  base: styles.grpHeadBase,
  build: styles.grpHeadBuild,
  competition: styles.grpHeadCompetition,
  taper: styles.grpHeadTaper,
  recovery: styles.grpHeadRecovery,
};

const PHASE_LABEL: Record<string, string> = {
  base: 'Base',
  build: 'Build',
  competition: 'Competition',
  taper: 'Taper',
  recovery: 'Recovery',
};

// Wave 2: the default link means "the season plan": `/plan` with `train_2_0_v1` off (as it has always been),
// `/plan/season` with it on, taken from `useSeasonPath()` inside the component.

export function SeasonRail({
  seasonName,
  weeks,
  currentWeek,
  approved = false,
  footLinks,
  currentWeekSuffix,
}: SeasonRailProps) {
  // Read before the early return (rules of hooks).
  const iconsOn = useIconSystem();
  const seasonPath = useSeasonPath();
  const links = footLinks ?? [{ to: seasonPath, label: 'Open season plan ›' }];
  if (weeks.length === 0) return null;
  const phases = groupPhases(weeks);
  const displayName = seasonName ?? 'Season';
  const summary = formatSummary(weeks);

  return (
    <aside
      className={styles.seasonRail}
      aria-label={`Season navigator · ${displayName}`}
      data-seasonrail="true"
    >
      <div className={styles.head}>
        <div className={styles.kicker}>
          <span>Season</span>
          {approved && <span className={styles.approved}>✓ Approved</span>}
        </div>
        <div className={styles.name}>{displayName}</div>
        {summary && <div className={styles.sub}>{summary}</div>}
      </div>

      {phases.map((phase) => {
        const groupClass = PHASE_GROUP_CLASS[phase.mode] ?? '';
        const wkCount = phase.endWeek - phase.startWeek + 1;
        return (
          <div
            key={`${phase.mode}-${phase.startWeek}`}
            className={styles.group}
            data-mode={phase.mode}
          >
            <div className={`${styles.grpHead} ${groupClass}`}>
              <span className={styles.swatch} aria-hidden />
              <span>{PHASE_LABEL[phase.mode] ?? phase.mode}</span>
              <span className={styles.meta}>
                {wkCount} {wkCount === 1 ? 'wk' : 'wks'}
              </span>
            </div>
            {phase.weeks.map((w) => {
              const isCurrent = currentWeek != null && w.week_number === currentWeek;
              const isPast = currentWeek != null && w.week_number < currentWeek;
              const dateLabel = formatWeekDate(w.week_start_date);
              const dateSuffix = isCurrent && currentWeekSuffix ? currentWeekSuffix : '';
              const cls = [
                styles.rWeek,
                isCurrent ? styles.rWeekCurrent : '',
                isPast ? styles.rWeekPast : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <Link
                  key={w.week_number}
                  to={`/plan/week/${w.week_number}`}
                  className={cls}
                  aria-current={isCurrent ? 'true' : undefined}
                >
                  <span className={styles.wn}>W{w.week_number}</span>
                  <span className={styles.wd}>
                    {dateLabel ?? '—'}
                    {dateSuffix}
                  </span>
                  <span className={styles.meet} aria-hidden>
                    {/* A2 — `mark.meet` @12 in the week row's meet cell. */}
                    {w.meet_id ? (iconsOn ? <Icon name="mark.meet" size={12} /> : '🏁') : ''}
                  </span>
                </Link>
              );
            })}
          </div>
        );
      })}

      {links.length > 0 && (
        <div className={styles.foot}>
          {links.map((link) => (
            <Link key={link.to} to={link.to} className={styles.openLink}>
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </aside>
  );
}

function formatSummary(weeks: MacroPlanWeek[]): string | null {
  if (weeks.length === 0) return null;
  const first = weeks[0]?.week_start_date;
  const lastStart = weeks[weeks.length - 1]?.week_start_date;
  const startLabel = first ? formatWeekDate(first) : null;
  const endLabel = lastStart ? formatWeekDateAddDays(lastStart, 6) : null;
  // "Aug 31 to Oct 18", not an en dash (register, UX round 1), and the month
  // once inside one month — "Sep 7 to 13", the shared rule in
  // `lib/formatWeekRange` (register pass 2026-09-09, critic 9). The current
  // week's " — now" is the caller's `currentWeekSuffix`, ratified by
  // R-Ultra-1 as it stands: it is a different string and stays.
  const range =
    startLabel && endLabel && startLabel !== endLabel
      ? (formatWeekRange(first, isoAddDays(lastStart!, 6)) ??
        `${startLabel} to ${endLabel}`)
      : startLabel ?? '';
  const weekCount = `${weeks.length} ${weeks.length === 1 ? 'week' : 'weeks'}`;
  const meetCount = weeks.filter((w) => !!w.meet_id).length;
  // B91 — no leading separator here: `join(' · ')` below is the only one.
  const meetsPart =
    meetCount === 0 ? '' : `${meetCount} ${meetCount === 1 ? 'meet' : 'meets'}`;
  return [weekCount, range, meetsPart].filter(Boolean).join(' · ');
}

/** The week's last day as an ISO date — the same local-TZ arithmetic, kept
 *  as a date so `formatWeekRange` can decide whether the month repeats. */
function isoAddDays(iso: string, days: number): string | null {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function formatWeekDateAddDays(iso: string, days: number): string | null {
  const end = isoAddDays(iso, days);
  return end ? formatWeekDate(end) : null;
}

export default SeasonRail;
